import styled from 'styled-components';

import classNames from "classnames";
import {useToast} from "@chakra-ui/react";
import React, {useState} from "react";
import {Award as LucideAward, Check, Ellipsis, Eraser, Hand, Handshake, Pin} from "lucide-react";
import {useRelativeTime, useStoreCommunity, useStoreProfile} from '../store';
import useModal from "../modals";
import useWindowDimensions, {useDimensions, useVideoDimensions} from "../utils/dimensions";
import {themecolors} from "../utils/themecolors";
import {useScoredMedia} from "../utils/host";
import {useCommunitySubscribe} from "../api/communities";
import useFeedback from "../utils/use_feedback";
import useProfilePicture from "../utils/profile_picture";
import NavLink from './nav_link';
import CommunityLogo from './community_logo';
import Skeleton from './skeleton';
import ExternalLink from "./external_link";
import RoleIndicator from "./role_indicator";
import ViewableImage from "./viewable_image";
import {BareButton} from "./button";
import Sheet from "./bottom-sheet/sheet";
import PostActionsSheet from "./bottom-sheet/post_actions";
import PostExpand from "./post_expand";
import SeparatorBullet from "./separator_bullet";
import ProfilePicture from "./profile_picture";

const Infos = styled.div``;
const Title = styled.div``;
const Logo = styled.div``;
const Details = styled.div``;
const Content = styled.div``;
const ContentPreview = styled.div``;
const Stickied = styled.div``;
const Options = styled.div``;
const Edited = styled.span``;
const NewUser = styled.span``;
const Approved = styled.span``;
const VideoContainerOuter = styled.div``;
const VideoContainerInner = styled.div``;
const Preview = styled.div``;
const TextAndPreview = styled.div``;
const Award = styled.div``;
const CommunityFeed = styled.div``;
const SubscribeButton = styled.div``;
const Avatar = styled.div``;
const CommunityAuthor = styled.div``;
const Dots = styled.div``;

const CardPost = styled(({className, post, children, hideEmbeds, inFeed, sort, isCommunityFeed, isTrending = false, mainFeedIndex = false}) => {
    const {theme} = useStoreProfile();
    const community = useStoreCommunity(post?.community);
    const ago = useRelativeTime((sort && sort === 'active') ? post?.last_comment_created: post?.created);
    const modal = useModal();
    const reported = post?.moderation && post.moderation.is_reported;
    const removed = post?.moderation && post.moderation.removed_at !== 0;
    const windowWidth = useWindowDimensions().width;
    const compressedMedia = post?.media.resized_resolutions[0] || false;
    const isScoredMedia = useScoredMedia(post?.link);
    const dimensions = useDimensions(windowWidth - (inFeed ? 32 : 0), compressedMedia?.width || 0, compressedMedia?.height || 0);
    const contentType = compressedMedia?.["content-type"];

    const videoDimensions = useVideoDimensions(post?.media, windowWidth - (inFeed ? 32 : 0));
    const isVideo = post?.is_video || post?.link_metadata?.rumble_embed;
    const isVidey = post?.is_video && !post?.is_video_mp4 && post.video_link.startsWith("https://cdn.videy.co/");
    const iframeLink = post && !isVidey && ((post.is_video && !post.is_video_mp4 && post.video_link) || post.link_metadata?.rumble_embed);

    const isMetadata = (!post?.content && !isVideo && !post?.is_image && !post?.is_twitter && !post?.is_crosspost) && (post && post?.link_metadata?.title);
    const isPlaceholderLink = post && !post?.content && !isVideo && !post?.is_image && !post?.is_twitter && !post?.is_crosspost && !isMetadata;

    const {feedbackCh} = useFeedback();
    const subscribe = useCommunitySubscribe(feedbackCh);
    const toast = useToast();
    const handleSubscribe = async (event) => {
        event.preventDefault();
        try {
            await subscribe([community.name]);
        } catch {
            toast({
                title: `Failed to subscribe.`,
                status: 'error',
                isClosable: true,
            });
        }
    }

    const [actionsOpen, setActionsOpen] = useState(false);
    const AuthorWrapper = isCommunityFeed ? CommunityAuthor : null;

    return (
        <div className={classNames(className, {removed, reported})}>
            <Infos style={{height: !isCommunityFeed ? 40 : 30, marginBottom: !isCommunityFeed ? 4 : 4}}>
                {!isCommunityFeed &&
                    <Logo>
                        {post
                            ? <CommunityLogo name={post?.community}/>
                            : <Skeleton circle height={32} width={32}/>}
                    </Logo>
                }
                <Details>
                    {post
                      ? <>
                            {!isCommunityFeed &&
                                <CommunityFeed>
                                    <NavLink href={`/c/${post.community}`}>{post.community}</NavLink>
                                </CommunityFeed>
                            }
                            {!inFeed || (inFeed && isCommunityFeed) &&
                                <Avatar>
                                    <ProfilePicture size={24} fontSize={13} username={post.author} profilePicture={useProfilePicture(post.author, post.profile_picture, theme)} />
                                </Avatar>
                            }
                            {isCommunityFeed ?
                                <CommunityAuthor>
                                    <NavLink href={`/u/${post.author}`}>{post.author}</NavLink>
                                </CommunityAuthor>
                                :
                                <NavLink href={`/u/${post.author}`}>{post.author}</NavLink>
                            }
                         <RoleIndicator admin={post.is_admin} mod={post.is_moderator} proTier={post.pro_tier} />
                         <SeparatorBullet />
                         {ago} ago
                            {post?.awards > 0 &&
                            <Award>
                                <LucideAward size={14} color={"#dcba00"}/>
                            </Award>
                            }
                         {post?.is_edited && (
                            <Edited>
                                <Eraser size={14} />
                            </Edited>
                         )}
                         {post?.is_new_user && (
                            <NewUser>
                                <Hand size={14} />
                            </NewUser>
                         )}
                         {post?.moderation && post.moderation?.approved_at !== 0 && <Approved><Check size={16} /></Approved>}
                        </>
                     : <Skeleton height={12} count={2} />}
                </Details>
                <Options>
                    {!isCommunityFeed && inFeed && community.created && !community?.is_subscribed &&
                        <BareButton onClick={handleSubscribe}>
                            <SubscribeButton>
                                Join
                            </SubscribeButton>
                        </BareButton>
                    }
                    {post?.is_stickied &&
                        <Stickied>
                            <Pin size={14} color={"#ffbe00"} fill={"#ffbe00"} />
                        </Stickied>
                    }
                    <BareButton onClick={() => setActionsOpen(true)}>
                        <Dots>
                            <Ellipsis size={18}/>
                        </Dots>
                    </BareButton>
                </Options>
            </Infos>
            <TextAndPreview>
                <Title>
                        {post
                            ? (post.link && !inFeed
                                ? <ExternalLink href={post.link}>
                                    {post.title}
                                  </ExternalLink>
                                : post.title
                              )
                            : <Skeleton />
                        }
                </Title>
                {post?.preview && (!post?.content && !isVideo && !post?.is_image && !post?.is_twitter && !isMetadata) && (
                    <Preview style={{filter: post?.is_nsfw && "blur(4px)"}}>
                        <ViewableImage post={post} preview={post.preview} src={post.link} alt="post" title={post.title} author={post.author} community={post.community} viewable={post.is_image} />
                    </Preview>
                )}
            </TextAndPreview>
            <PostExpand post={post} inFeed={inFeed} />
            {children}
            {actionsOpen && <Sheet onClose={() => setActionsOpen(false) }  padding={"menu"}><PostActionsSheet isTrending={isTrending} postId={post?.id} onClose={() => setActionsOpen(false) } /></Sheet>}
        </div>
    );
})`
  width: 100%;
  background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_1 : theme.white_0};
  padding-top: ${({isCommunityFeed}) => isCommunityFeed ? 10 : 10}px;
  padding-bottom: ${({isCommunityFeed}) => isCommunityFeed ? 4 : 4}px;
  
  ${Avatar} {
    width: 20px;
    margin-right: 8px;
  }
  ${Avatar} img {
    border-radius: 50%;
  }
  
  ${VideoContainerOuter} {
    max-width: 560px;
    margin: 0 auto;
  }
  
  ${VideoContainerInner} {
    position: relative;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;
    max-width: 560px;
  }

  ${VideoContainerInner} iframe,
  ${VideoContainerInner} object,
  ${VideoContainerInner} embed { 
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  
  iframe {
    display: flex;
    margin: 0 auto;
  }

  video {
    max-width: 100%;
  }
  
  & img {
    max-width: 100%;
    display: flex;
    margin: 0 auto;
  }

  ${Infos} {
    height: 40px;
    padding: 0 16px;
    font-size: ${({isCommunityFeed}) => isCommunityFeed ? 12 : 13}px;
    min-height: 26px;
    display: flex;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_low_impact_text : theme.light_low_impact_text};
    align-items: center;
  }

  ${Title} {
    padding: 0 16px;
    font-size: 15px;
    font-weight: 600;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_high_impact_text : themecolors.light_high_impact_text};
    word-break: break-word;
  }
  ${Title} svg {
    font-size: 10px;
    margin-left: 2px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_high_impact_text : themecolors.light_high_impact_text};
  }

  ${Logo} {
       flex: 0 0 32px;
       height: 32px;
       margin-right: 10px;
     }

  ${Logo} img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }

  ${Details} {
    flex-grow: 1;
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    white-space: break-spaces;
    font-weight: 400;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_low_impact_text};
  }
  
  ${Details} ${Skeleton} {
    flex: 0 0 120px;
  }

  ${CommunityFeed} a {
    font-size: 14px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_high_impact_text : theme.post.primary};
    font-weight: 600;
  }
  ${CommunityFeed} {
    width: 100%;
    margin-bottom: -4px;
  }

  ${Stickied} {
    margin-left: 6px;
    margin-right: 8px;
  }
  
  ${Content} {
    margin-top: 8px;
    font-size: 15px;
    padding: 0 16px 6px 16px;
    overflow-x: hidden;
    border-radius: 4px;
    line-height: 23px;
  }
  ${Content} p:last-of-type {
    margin-bottom: 0;
  }
  ${ContentPreview} {
    font-size: 15px;
    max-height: 102px;
    overflow: hidden;
    position: relative;
    text-overflow: ellipsis;
    ${({theme: { isSafari }}) => !isSafari && `
    display: -webkit-box;
    -webkit-line-clamp: 5; /* number of lines to show */
    -webkit-box-orient: vertical;
    `}
  }
  ${ContentPreview}:after {
    content: "";
    position: absolute;
    top: 62px;
    width: 100%;
    left: 0;
    height: 40px;
    background: linear-gradient(rgba(0,0,0,0), ${({theme}) => theme.color.name === 'dark' ? `${themecolors.dark_background_1}` : theme.white_0});
  }
  ${Options} {
    margin-left: auto;
    display: flex;
    align-items: center;
  }
  
  ${NewUser}, ${Edited} {
    display: flex;
    font-size: 12px;
    margin-left: 8px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_low_impact_text : themecolors.light_low_impact_text};
  }

  ${Approved} {
    color: ${({theme}) => themecolors.green};
    margin-left: 8px;
    font-size: 13px;
  }

  ${Award} {
    display: flex;
    margin-left: 8px;
  }
  &.removed {
    background-color: ${({theme}) => theme.color.name === 'dark' ? theme.pink_2 : theme.pink_1};
  }
  &.reported {
    background-color: ${({theme}) => theme.color.name === 'dark' ? theme.yellow_2 : theme.yellow_1};
  }
  ${TextAndPreview} {
    display: flex;
  }
  ${Preview} {
    width: 82px;
    height: 62px;
    margin-left: auto;
    margin-right: 10px;
    overflow: hidden;
    text-align: right;
    flex-shrink: 0;
    border-radius: 8px;
    position: relative;
  }
  ${Preview} img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
  ${SubscribeButton} {
    font-size: 14px;
    font-weight: 600;
    background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_4 : '#000000'};
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_high_impact_text : '#e9e9e9'};
    padding: 4px 12px;
    border-radius: 48px;
    margin-right: 12px;
  }
  ${CommunityAuthor} {
    font-weight: 600;
    margin-left: 4px;
    font-size: 13px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text: themecolors.light_medium_impact_text};
  }
  ${Dots} {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
    display: flex;
    align-items: center;
  }
`;

export default CardPost;
